import { useRef } from 'react'
import type { FC } from 'react'
import {
  Box,
  Button,
  Grid,
  TextField,
  TextFieldProps,
  InputAdornment,
  IconButton,
} from '@mui/material'
import type { GridSize } from '@mui/material/Grid'
import FilterNoneIcon from '@mui/icons-material/FilterNone'
import omit from 'lodash/omit'

import { copyToClipboard } from '~/shared/lib/clipboard'

type EditTextFieldProps = TextFieldProps & { label?: string } & { showChangeButton?: boolean } & {
  minDate?: string
} & { changeButtonLabel?: string } & { changeButtonSize?: 'medium' | 'large' | 'small' } & {
  changeButtonDisabled?: boolean
} & { onChangeButtonClick?: () => void } & { gridSizeOverride?: GridSize }

export const EditField: FC<EditTextFieldProps> = props => {
  const ref = useRef<HTMLInputElement>(null)

  // If the EditField field is type of date then set a default past value
  const inputProps = {
    ...(props?.type == 'date' && {
      // If the date field have default minDate value then set that else disable all dates years before 1900
      min: props?.minDate ? props?.minDate : '1900-01-01',
    }),
  }

  // The caller's own slot props. EditField reads `input.readOnly` to drive `disabled`
  // and merges its clipboard adornment into the same slot, so the two have to be
  // combined by hand — a slot can also be given as a callback, which carries no props
  // to read, hence the narrowing.
  const callerSlotProps = props.slotProps ?? {}
  const callerInput =
    typeof callerSlotProps.input === 'function' ? undefined : callerSlotProps.input
  const callerHtmlInput =
    typeof callerSlotProps.htmlInput === 'function' ? undefined : callerSlotProps.htmlInput

  return (
    <Grid key={props.key} size={props.gridSizeOverride ? props.gridSizeOverride : 6}>
      {props.showChangeButton ? (
        <TextField
          fullWidth
          name={props.name}
          value={props.value ?? ''}
          label={props.label}
          disabled={callerInput?.readOnly}
          variant={props.variant ?? 'standard'}
          onChange={props.onChange}
          slotProps={{
            htmlInput: inputProps,
            input: {
              endAdornment: (
                <Button
                  disabled={props.changeButtonDisabled ? props.changeButtonDisabled : false}
                  color="primary"
                  onClick={props.onChangeButtonClick}
                  size={props.changeButtonSize ? props.changeButtonSize : 'medium'}
                >
                  {props.changeButtonLabel ? props.changeButtonLabel : 'Change'}
                </Button>
              ),
            },
          }}
        />
      ) : (
        <TextField
          fullWidth
          error={props.error}
          helperText={props.helperText}
          sx={{ '&:hover span': { opacity: 1 } }}
          inputRef={ref}
          variant={props.variant ? props.variant : 'standard'}
          slotProps={{
            // Caller slots first so anything EditField does not set survives, then its
            // own two back, with the caller's values still winning inside each.
            ...callerSlotProps,
            htmlInput: { ...inputProps, ...callerHtmlInput },
            // Don't append InputAdornment for copy/paste when rendering a <select> list
            input: !props.select
              ? {
                  classes: {
                    input: `TEST__${props.name}`,
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box component="span" sx={{ opacity: 0 }}>
                        <IconButton
                          onClick={() => {
                            if (ref.current) copyToClipboard(ref.current.value)
                          }}
                          size="small"
                        >
                          <FilterNoneIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                  ...callerInput,
                }
              : callerInput,
          }}
          // Spread the remaining TextField props, but drop the EditField-only props
          // (and value/variant, handled above) so they don't leak onto the DOM input
          // or override the standard-variant default.
          value={props.value ?? ''}
          {...omit(props, [
            'slotProps',
            'value',
            'variant',
            'minDate',
            'gridSizeOverride',
            'showChangeButton',
            'changeButtonLabel',
            'changeButtonSize',
            'changeButtonDisabled',
            'onChangeButtonClick',
          ])}
        />
      )}
    </Grid>
  )
}
